import { useEffect, useState } from "react";
import { CHECKOUT_STAGE } from "../stages/checkoutStages";
import { validateCheckout } from "../stages/validateCheckout";
import { initializeCheckout } from "../stages/initializeCheckout";

/**
 * ORCHESTRATION
 * Custom hook to manage the checkout stage, and render the proper UI.
 * @param {Object} options - The configuration options for the checkout hook.
 * @param {Object} options.service - The service to identify API endpoint and models(E.g: FI -> Financial Insights, TF -> Tax Filing)
 * @param {string} options.billingType - Identifier for Recurrent(SetupIntent) and One Time(PaymentIntent) payments
 * @param {string|number} options.purchaseId - This is the service id, used for API handling endpoint and models(E.g: subscription id, tax filing id).
 * @param {string|null} options.redirectStatus - This is the Stripe redict status from the payment/setup intent. User to route between payment elements and management(subscribed, processing, error)
 * @param {boolean|false} options.isUpgradeDowngradePlan - When upgrading a subscription plan.
 * @param {Function} options.navigate - The navigation function (typically from `react-router-dom` or `next/router`) to route the user after processing.
 * @returns {Object} An object containing state variables and handler functions to manage the checkout UI.
 */
export function useCheckout({
  service,
  billingType,
  purchaseId,
  redirectStatus,
  isUpgradeDowngradePlan,
  navigate,
}) {
  const [stage, setStage] = useState(CHECKOUT_STAGE.LOADING);
  const [clientSecret, setClientSecret] = useState("");
  const [gatewayStatus, setGatewayStatus] = useState(null);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);

  useEffect(() => {
    async function bootstrapCheckout() {
      try {
        // ======================================================
        // VALIDATE CHECKOUT STATE
        // ======================================================
        const validation = await validateCheckout({
          service,
          purchaseId,
          billingType,
          redirectStatus,
          isUpgradeDowngradePlan,
        });

        if (validation.summary) {
          setSummaryData(validation.summary);
        }

        // ======================================================
        // REDIRECT / VERIFY FLOW
        // ======================================================
        if (validation.stage === CHECKOUT_STAGE.VERIFYING) {
          setStage(CHECKOUT_STAGE.VERIFYING);
          return;
        }

        // ======================================================
        // PAYMENT FLOW
        // ======================================================
        if (validation.stage === CHECKOUT_STAGE.PAYMENT) {
          const result = await initializeCheckout({
            billingType,
            purchaseId,
          });

          setClientSecret(result.clientSecret || "bypass");
          setGatewayStatus(result.gatewayStatus);
          setStage(CHECKOUT_STAGE.PAYMENT);
          return;
        }

        // ======================================================
        // FALLBACK
        // ======================================================
        setStage(validation.stage);
      } catch (error) {
        console.error("Checkout orchestration exception:", error);

        setError(error.message || "Unexpected checkout exception.");

        setStage(CHECKOUT_STAGE.ERROR);
      }
    }

    bootstrapCheckout();
  }, [
    service,
    billingType,
    purchaseId,
    redirectStatus,
    navigate,
    isUpgradeDowngradePlan,
  ]);

  return {
    stage,
    clientSecret,
    gatewayStatus,
    error,
    summaryData,
  };
}
